const formatPayrollMoney = (amount: number | string | undefined | null): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('ko-KR').format(Math.round(num || 0));
};

const parseAmount = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  return parseInt(String(value).replace(/,/g, ''), 10) || 0;
};

const pct = (rate: number): string => (rate * 100).toFixed(3).replace(/\.?0+$/, '');

const getSupportBase = (settings: any) => {
  const longServiceAllowanceAmount = parseAmount(settings.longServiceAllowance);
  const welfarePayAmount =
    settings.welfarePay && settings.welfarePaySupportDeduct
      ? parseAmount(settings.welfarePay)
      : 0;
  return {
    longServiceAllowanceAmount,
    welfarePayAmount,
    base: longServiceAllowanceAmount + welfarePayAmount,
  };
};

export const buildSabokDeductionTooltip = (
  field: string,
  settings: any,
  taxData: any
): string => {
  if (!settings) return '';

  const fmt = formatPayrollMoney;
  const ins = settings.insurance || {};
  const tax = settings.tax || {};
  const taxableAmount = settings.totals?.taxableAmount || 0;

  switch (field) {
    case 'nationalPension':
      if (!ins.nationalPension?.deduct) return '미적용';
      if (ins.nationalPension?.manual) {
        return `국민연금 납부액(직접입력) = ${fmt(ins.nationalPension.amount || 0)}원`;
      }
      return `과세금액(${fmt(taxableAmount)}원) × ${pct(taxData?.tr_yeon || 0.045)}% = ${fmt(ins.nationalPension?.amount || 0)}원`;
    case 'healthInsurance':
      return `과세금액(${fmt(taxableAmount)}원) × ${pct(taxData?.tr_gun || 0.03545)}% = ${fmt(ins.healthInsurance?.amount || 0)}원`;
    case 'longTermCare':
      return `건강보험(${fmt(ins.healthInsurance?.amount || 0)}원) × ${pct(taxData?.tr_jang || 0.1295)}% = ${fmt(ins.longTermCare?.amount || 0)}원`;
    case 'employmentInsurance':
      if (!ins.employmentInsurance?.deduct) return '미적용';
      return `과세금액(${fmt(taxableAmount)}원) × ${pct(taxData?.tr_go || 0.009)}% = ${fmt(ins.employmentInsurance?.amount || 0)}원`;
    case 'incomeTax':
      return `간이세액표(과세금액 ${fmt(taxableAmount)}원) = ${fmt(tax.incomeTax || 0)}원`;
    case 'localTax':
      return `소득세(${fmt(tax.incomeTax || 0)}원) × 10% = ${fmt(tax.localTax || 0)}원`;
    case 'supportDeduction': {
      if (!settings.supportDeductionAuto) {
        return `지원공제(직접입력) = ${fmt(parseAmount(settings.supportDeductionManual))}원`;
      }

      const { longServiceAllowanceAmount, welfarePayAmount, base } = getSupportBase(settings);
      if (base <= 0) {
        return '지원공제 대상 없음\n\n장기근속수당 또는 처우개선비(지원공제 체크)가 있을 때 적용됩니다.';
      }

      const workHours = parseFloat(String(settings.workHours)) || 0;
      let tooltip = '[지원공제 계산]\n\n';
      tooltip += '▶ 기준금액\n';
      if (longServiceAllowanceAmount > 0) {
        tooltip += `   • 장기근속수당: ${fmt(longServiceAllowanceAmount)}원\n`;
      }
      if (welfarePayAmount > 0) {
        tooltip += `   • 처우개선비: ${fmt(welfarePayAmount)}원\n`;
      }
      tooltip += `   • 합계: ${fmt(base)}원\n\n`;

      tooltip += '▶ 세부 공제\n';
      if (ins.nationalPension?.deduct) {
        const amount = Math.floor((base * (taxData?.tr_yeon || 0)) / 10) * 10;
        tooltip += `   • 국민연금: ${fmt(base)}원 × ${pct(taxData?.tr_yeon || 0.045)}% = ${fmt(amount)}원\n`;
      }
      const health = Math.floor((base * (taxData?.tr_gun || 0)) / 10) * 10;
      tooltip += `   • 건강보험: ${fmt(base)}원 × ${pct(taxData?.tr_gun || 0.03545)}% = ${fmt(health)}원\n`;
      const ltc = Math.floor((health * (taxData?.tr_jang || 0)) / 10) * 10;
      tooltip += `   • 장기요양: ${fmt(health)}원 × ${pct(taxData?.tr_jang || 0.1295)}% = ${fmt(ltc)}원\n`;
      if (ins.employmentInsurance?.deduct) {
        const amount = Math.floor((base * (taxData?.tr_go || 0)) / 10) * 10;
        tooltip += `   • 고용보험: ${fmt(base)}원 × ${pct(taxData?.tr_go || 0.009)}% = ${fmt(amount)}원\n`;
      }
      if (ins.employmentInsurance?.deduct && ins.unemployment?.deduct !== false) {
        const amount = Math.floor((base * (taxData?.tr_sil || 0)) / 10) * 10;
        tooltip += `   • 실업급여: ${fmt(base)}원 × ${pct(taxData?.tr_sil || 0.0025)}% = ${fmt(amount)}원\n`;
      }
      if (ins.industrialAccident?.deduct !== false) {
        const amount = Math.floor((base * (taxData?.tr_san || 0)) / 10) * 10;
        tooltip += `   • 산재보험: ${fmt(base)}원 × ${pct(taxData?.tr_san || 0.0178)}% = ${fmt(amount)}원\n`;
      }
      if (workHours >= 60 && ins.retirement?.deduct !== false) {
        const amount = Math.floor(base / 12 / 10) * 10;
        tooltip += `   • 퇴직금: ${fmt(base)}원 ÷ 12 = ${fmt(amount)}원\n`;
      }

      const supportAmount = settings.supportDeductionAuto
        ? ins.supportDeduction?.amount
        : parseAmount(settings.supportDeductionManual);
      tooltip += `\n지원공제 합계: ${fmt(supportAmount || ins.supportDeduction?.amount || 0)}원`;
      return tooltip;
    }
    case 'empEtc':
      return `기타 공제(근로자) = ${fmt(ins.empEtc?.amount || 0)}원`;
    case 'totalDeduction':
      return `4대보험 + 세금 + 지원공제 + 기타공제 = ${fmt(
        (ins.nationalPension?.amount || 0) +
          (ins.healthInsurance?.amount || 0) +
          (ins.longTermCare?.amount || 0) +
          (ins.employmentInsurance?.amount || 0) +
          (tax.incomeTax || 0) +
          (tax.localTax || 0) +
          (ins.supportDeduction?.amount || 0) +
          (ins.empEtc?.apply ? ins.empEtc?.amount || 0 : 0)
      )}원`;
    default:
      return '';
  }
};

export const buildSabokCenterTooltip = (
  field: string,
  settings: any,
  taxData: any
): string => {
  if (!settings) return '';

  const fmt = formatPayrollMoney;
  const ins = settings.insurance || {};
  const taxableAmount = settings.totals?.taxableAmount || 0;
  const workHours = parseFloat(String(settings.workHours)) || 0;

  const timePay = parseAmount(settings.timePay);
  const baseSalary = Math.floor(timePay * workHours);
  const allowanceFields = [
    'overtimeAllowance', 'nightAllowance', 'holidayAllowance', 'annualLeaveAllowance',
    'longServiceAllowance', 'transportationFee', 'educationFee', 'bonus',
    'otherAllowance', 'mealAllowance', 'vehicleAllowance', 'childEducationFee', 'welfarePay',
  ];
  let totalAllowance = 0;
  allowanceFields.forEach((f) => {
    totalAllowance += parseAmount(settings[f]);
  });
  const totalPay = baseSalary + totalAllowance;

  switch (field) {
    case 'nationalPension':
      if (!ins.nationalPension?.deduct) return '미적용 (기관부담 없음)';
      if (ins.nationalPension?.manual) {
        return `국민연금 기관부담(근로자와 동일, 직접입력) = ${fmt(ins.nationalPension.amount || 0)}원`;
      }
      return `과세금액(${fmt(taxableAmount)}원) × ${pct(taxData?.tr_yeon || 0.045)}% = ${fmt(ins.nationalPension?.amount || 0)}원`;
    case 'healthInsurance':
      return `과세금액(${fmt(taxableAmount)}원) × ${pct(taxData?.tr_gun || 0.03545)}% = ${fmt(ins.healthInsurance?.amount || 0)}원`;
    case 'longTermCare':
      return `건강보험(${fmt(ins.healthInsurance?.amount || 0)}원) × ${pct(taxData?.tr_jang || 0.1295)}% = ${fmt(ins.longTermCare?.amount || 0)}원`;
    case 'employmentInsurance':
      if (!ins.employmentInsurance?.deduct) return '미적용';
      return `과세금액(${fmt(taxableAmount)}원) × ${pct(taxData?.tr_go || 0.009)}% = ${fmt(ins.employmentInsurance?.amount || 0)}원`;
    case 'unemployment':
      if (!ins.employmentInsurance?.deduct || ins.unemployment?.deduct === false) return '미적용';
      return `과세금액(${fmt(taxableAmount)}원) × ${pct(taxData?.tr_sil || 0.0025)}% = ${fmt(ins.unemployment?.amount || 0)}원`;
    case 'industrialAccident':
      if (ins.industrialAccident?.deduct === false) return '미적용 (대표자)';
      return `과세금액(${fmt(taxableAmount)}원) × ${pct(taxData?.tr_san || 0.0178)}% = ${fmt(ins.industrialAccident?.amount || 0)}원`;
    case 'retirement':
      if (workHours < 60) return '미적용 (근무시간 60시간 미만)';
      if (ins.retirement?.deduct === false) return '미적용 (대표자)';
      return `총급여(${fmt(totalPay)}원) ÷ 12 = ${fmt(ins.retirement?.amount || 0)}원`;
    case 'totalCenter':
      return `4대보험 + 실업급여 + 산재보험 + 퇴직금 = ${fmt(
        (ins.nationalPension?.amount || 0) +
          (ins.healthInsurance?.amount || 0) +
          (ins.longTermCare?.amount || 0) +
          (ins.employmentInsurance?.amount || 0) +
          (ins.unemployment?.amount || 0) +
          (ins.industrialAccident?.amount || 0) +
          (ins.retirement?.amount || 0)
      )}원`;
    default:
      return '';
  }
};
